import { useCallback, useEffect, useState } from 'react';

import type { SapConnectionView, TestSapConnectionParams, TestSapConnectionResult, UpsertSapConnectionParams } from '@/domain/models/sap-connection.js';
import type { SapConnectionController } from '@/presentation/controllers/sap-connection-controller.js';

type State = {
    connection: SapConnectionView | null;
    isLoading: boolean;
    error: string | null;
};

export function useSapConnection(controller: SapConnectionController, clientId: string | undefined, enabled: boolean) {
    const [state, setState] = useState<State>({ connection: null, isLoading: false, error: null });

    const load = useCallback(async () => {
        if (!clientId || !enabled) {
            setState({ connection: null, isLoading: false, error: null });
            return;
        }
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const result = await controller.get(clientId);
        result.fold(
            (error) => setState({ connection: null, isLoading: false, error: error.message }),
            (connection) => setState({ connection, isLoading: false, error: null })
        );
    }, [clientId, controller, enabled]);

    useEffect(() => {
        void load();
    }, [load]);

    const upsert = useCallback(
        async (params: UpsertSapConnectionParams) => {
            if (!clientId) {
                return null;
            }
            const result = await controller.upsert(clientId, params);
            if (result.isRight()) {
                setState({ connection: result.value, isLoading: false, error: null });
            }
            return result;
        },
        [clientId, controller]
    );

    const test = useCallback(
        async (params: TestSapConnectionParams): Promise<TestSapConnectionResult | { error: string }> => {
            if (!clientId) {
                return { error: 'client-required' };
            }
            const result = await controller.test(clientId, params);
            if (result.isLeft()) {
                return { error: result.value.message };
            }
            return result.value;
        },
        [clientId, controller]
    );

    return { ...state, reload: load, upsert, test };
}
